import { colorMiscBlue70, IconX } from "@rhumbix/rmbx_design_system_web"
import { forwardRef, useImperativeHandle, useState, useRef, KeyboardEvent, useEffect, useMemo } from "react"
import { JsonPointer as jsonpointer } from "json-ptr"

import { cleanCommaSeparatedEmails, isValidEmailString, cleanEmailAddress } from "../../../common/ts-utils"
import { ICellEditorParams } from "ag-grid-community"
import {
    TagEditorContainer,
    TagsContainer,
    Tag,
    TagText,
    RemoveButton,
    TagInput,
    ErrorMessage,
} from "./tag-editor-styles"
import { useSuggestions, SuggestionsDropdown } from "./useSuggestions"

const WARNING_MESSAGE_DURATION = 3000 // 3 seconds

const EmailTagCellEditor = forwardRef((props: ICellEditorParams, ref) => {
    // Parse the comma-separated email string into an array
    const parseEmails = (emailString: string): string[] => {
        if (!emailString || typeof emailString !== "string") return []

        return cleanCommaSeparatedEmails(emailString)
    }

    const [emails, setEmails] = useState<string[]>(parseEmails(props.value))
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Helper function to show temporary error messages
    const showTemporaryError = (message: string) => {
        // Clear any existing timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
        }

        setError(message)

        // Set new timeout and store its reference
        errorTimeoutRef.current = setTimeout(() => {
            setError("")
            errorTimeoutRef.current = null
        }, WARNING_MESSAGE_DURATION)
    }

    // Collect all unique emails from all rows (recomputed only when grid/field changes)
    const allEmails = useMemo(() => {
        const uniqueEmailsFromGrid = new Set<string>()

        props.api.forEachNode((node: any) => {
            const fieldName = props?.colDef?.field
            if (!fieldName) return

            let rowEmails = ""

            // if the field name starts with a slash, try finding it using jsonpointer
            if (fieldName.startsWith("/")) {
                rowEmails = (jsonpointer.get(node?.data ?? {}, fieldName) ?? "") as string
            }

            // if the field doesn't have a slash or jsonpointer doesn't find it, try the path as-is
            if (!rowEmails) {
                rowEmails = node?.data?.[fieldName] ?? ""
            }

            if (rowEmails && typeof rowEmails === "string") {
                cleanCommaSeparatedEmails(rowEmails).forEach((email: string) => {
                    const trimmed = email.trim()
                    if (trimmed) {
                        uniqueEmailsFromGrid.add(trimmed)
                    }
                })
            }
        })

        return uniqueEmailsFromGrid
    }, [props.api, props?.colDef?.field])

    const allEmailsArray = useMemo(() => Array.from(allEmails), [allEmails])

    const suggestionState = useSuggestions({
        allSuggestions: allEmailsArray,
        inputValue,
        excludeValues: emails,
        onSelect: (email: string) => {
            setEmails(prev => [...prev, email])
            setInputValue("")
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
    })

    // Focus the input when the editor opens
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                // Add any remaining input as an email before returning
                if (inputValue.trim()) {
                    const finalEmails = [...emails]
                    const cleanedEmail = cleanEmailAddress(inputValue)
                    if (!emails.includes(cleanedEmail)) {
                        finalEmails.push(cleanedEmail)
                    }
                    return finalEmails.join(", ")
                }
                // Convert the array back to a comma-separated string
                return emails.join(", ")
            },
            afterGuiAttached: () => {
                // Focus the input after the component is attached to the DOM
                setTimeout(() => {
                    inputRef.current?.focus()
                }, 0)
            },
        }
    })

    // focus on mount
    useEffect(() => {
        // Small timeout to ensure the DOM is ready
        const timer = setTimeout(() => {
            inputRef.current?.focus()
        }, 10)

        return () => {
            clearTimeout(timer)
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current)
            }
        }
    }, [])

    /**
     * Removing one email at a time from the list
     * @param index
     */
    const removeEmail = (index: number) => {
        const newEmails = emails.filter((_, i) => i !== index)
        setEmails(newEmails)
    }

    /**
     * Adding one email at a time to the list
     * Show a warning if the email appears invalid, but still add it
     * Let the backend do the actual validation
     * @param email
     */
    const addEmail = (email: string) => {
        const cleanedEmail = cleanEmailAddress(email)
        if (!cleanedEmail) return

        // Check for duplicates
        if (emails.includes(cleanedEmail)) {
            showTemporaryError(`"${cleanedEmail}" is already in the list`)
            return
        }

        // Show a warning if email appears invalid, but still add it
        // Backend will do the actual validation
        if (!isValidEmailString(cleanedEmail)) {
            showTemporaryError(`"${cleanedEmail}" may not be a valid email`)
        }

        setEmails(prev => [...prev, cleanedEmail])
        setInputValue("")
    }

    /**
     * Handle paste event, which will set multiple emails at once
     * Show warnings for invalid emails but still add them
     * Let the backend do the actual validation
     * @param e
     */
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData("text")
        const pastedEmails = cleanCommaSeparatedEmails(pastedText)

        const newEmails: string[] = []
        const duplicates: string[] = []
        const invalidEmails: string[] = []

        pastedEmails.forEach(email => {
            const cleanedEmail = cleanEmailAddress(email)
            if (!cleanedEmail) return

            if (emails.includes(cleanedEmail)) {
                duplicates.push(cleanedEmail)
            } else {
                newEmails.push(cleanedEmail)
                // Check for invalid emails but still add them
                if (!isValidEmailString(cleanedEmail)) {
                    invalidEmails.push(cleanedEmail)
                }
            }
        })

        // Add all new emails (including invalid ones for backend validation)
        if (newEmails.length > 0) {
            setEmails(prev => [...prev, ...newEmails])
            setInputValue("")
        }

        // Show appropriate warnings
        if (duplicates.length > 0) {
            showTemporaryError(`Skipped duplicate(s): ${duplicates.join(", ")}`)
        } else if (invalidEmails.length > 0) {
            showTemporaryError(`${invalidEmails.join(", ")} may not be valid`)
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // Delegate to suggestion navigation first
        if (suggestionState.handleSuggestionKeyDown(e)) return

        // Handle keys when no suggestions are shown (or suggestion didn't handle Enter)
        if (e.key === " " || e.key === "," || e.key === ";") {
            e.preventDefault()
            if (inputValue.trim()) {
                addEmail(inputValue)
            }
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (inputValue.trim()) {
                addEmail(inputValue)
            }
        }
        // Remove last email on Backspace if input is empty
        else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
            removeEmail(emails.length - 1)
        }
    }

    return (
        <TagEditorContainer>
            <TagsContainer>
                {emails.map((email, index) => (
                    <Tag key={index}>
                        <TagText>{email}</TagText>
                        <RemoveButton
                            onClick={() => removeEmail(index)}
                            type="button"
                            aria-label={`Remove ${email}`}
                        >
                            <IconX color={colorMiscBlue70} height={14} width={14} />
                        </RemoveButton>
                    </Tag>
                ))}
                <TagInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    onChange={e => {
                        setInputValue(e.target.value)
                        suggestionState.setSelectedSuggestionIndex(-1)
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyDownCapture={suggestionState.handleSuggestionKeyDownCapture}
                    onPaste={handlePaste}
                    onBlur={e => {
                        // Check if blur is caused by clicking on a suggestion
                        if (suggestionState.isSuggestionBlur(e)) return
                        // Hide suggestions after a small delay to allow click events
                        setTimeout(() => {
                            suggestionState.setShowSuggestions(false)
                            // Add any remaining email when losing focus
                            const cleanedEmail = cleanEmailAddress(inputValue)
                            if (cleanedEmail) {
                                addEmail(cleanedEmail)
                            }
                        }, 200)
                    }}
                    onFocus={suggestionState.handleSuggestionFocus}
                    placeholder={emails.length === 0 ? "Type email and press space..." : "Add more..."}
                />
            </TagsContainer>
            <SuggestionsDropdown {...suggestionState} />
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </TagEditorContainer>
    )
})

export default EmailTagCellEditor
